const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-2);
    vertical-align: middle;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    color: var(--ctl-color-muted);
  }

  .ring{
    width: var(--sz);
    height: var(--sz);
    border-radius: var(--ctl-radius-full);
    border: var(--ctl-border-width-thick) solid color-mix(in srgb, var(--ctl-color-border) 85%, transparent);
    border-top-color: var(--ctl-color-brand);
    animation: spin 0.8s linear infinite;
    flex: 0 0 auto;
  }

  .label{
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-extrabold);
    letter-spacing: var(--ctl-tracking-tight);
    line-height: var(--ctl-leading-tight);
    color: var(--ctl-color-muted);
  }

  /* sizes */
  :host([size="sm"]){ --sz: var(--ctl-space-3-5); }
  :host(:not([size]), [size="md"]){ --sz: var(--ctl-space-4-5); }
  :host([size="lg"]){ --sz: var(--ctl-size-icon-lg); }

  @keyframes spin { to { transform: rotate(360deg); } }

  /* If no label, hide the label element */
  :host([no-label]) .label{ display:none; }
</style>

<span class="ring" part="ring" aria-hidden="true"></span>
<span id="label" class="label" part="label"></span>
`;

export class CtlSpinner extends HTMLElement {
  static get observedAttributes() {
    return ["size", "label"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._labelEl = this.shadowRoot.getElementById("label");
  }

  connectedCallback() {
    if (!this.hasAttribute("size")) this.setAttribute("size", "md");
    this._sync();
  }

  attributeChangedCallback() {
    this._sync();
  }

  get size() { return this.getAttribute("size") || "md"; }
  set size(v) {
    const s = (v === "sm" || v === "lg") ? v : "md";
    this.setAttribute("size", s);
  }

  get label() { return this.getAttribute("label") || ""; }
  set label(v) {
    v == null || v === "" ? this.removeAttribute("label") : this.setAttribute("label", String(v));
  }

  _sync() {
    // normalize size
    const s = this.size;
    if (!["sm", "md", "lg"].includes(s)) this.setAttribute("size", "md");

    const label = (this.getAttribute("label") || "").trim();
    const has = !!label;

    this.toggleAttribute("no-label", !has);

    if (has) {
      this._labelEl.textContent = label;

      // a11y: announce label text politely
      this.setAttribute("role", "status");
      this.setAttribute("aria-live", "polite");
      this.removeAttribute("aria-hidden");
      this.setAttribute("aria-label", label);
    } else {
      this._labelEl.textContent = "";

      // a11y: decorative spinner; rely on surrounding context to announce
      this.removeAttribute("role");
      this.removeAttribute("aria-live");
      this.removeAttribute("aria-label");
      this.setAttribute("aria-hidden", "true");
    }
  }
}

customElements.define("ctl-spinner", CtlSpinner);
